import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {AudioItem} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';

/**
 * Max time difference in seconds that the audio and video can be out of sync
 */
const MAXIMUM_SYNC_THRESHOLD = 0.5;

export class SyncAudioToPageClock extends CommonMethods {
  public audioItem: AudioItem;
  private syncInFlight = false;
  private pendingPageTime: number | null = null;
  private readonly boundSyncToPage;

  constructor(audioItem: AudioItem) {
    super();
    this.audioItem = audioItem;
    this.boundSyncToPage = this.syncToPage.bind(this);
  }

  public initSyncToPageClock(): void {
    this.audioItem.audioClips.page.on('time:updated', this.boundSyncToPage);
  }

  public unload(): void {
    this.audioItem.audioClips.page.off('time:updated', this.boundSyncToPage);
  }

  // Queue-of-one, mirroring StreamingItem: dropping intermediate ticks is fine, but the LAST
  // tick must always be processed — a plain re-entrancy bail can land mid-seek and leave the
  // audio in the play state of an already-stale tick (e.g. still playing after a pause).
  public async syncToPage(pageTime: number): Promise<void> {
    this.pendingPageTime = pageTime;
    if (this.syncInFlight) {
      return;
    }

    this.syncInFlight = true;
    try {
      // Sequential by design: each iteration must finish before re-reading pendingPageTime.
      while (this.pendingPageTime !== null) {
        const t = this.pendingPageTime;
        this.pendingPageTime = null;
        // eslint-disable-next-line no-await-in-loop
        await this.syncItemToTime(t);
        this.syncAudioPlaybackState();
      }
    } finally {
      this.syncInFlight = false;
    }
  }

  private async syncItemToTime(pageTime: number): Promise<void> {
    if (pageTime < this.audioItem.onPageStartTime || pageTime > this.audioItem.getOnPageEndTime()) {
      return;
    }

    const audioCurrentPlaybackTimeRespectiveToPage = this.audioItem.audioPlayer.getPlaybackCurrentTime() + this.audioItem.onPageStartTime;

    if (Math.abs(pageTime - audioCurrentPlaybackTimeRespectiveToPage) > MAXIMUM_SYNC_THRESHOLD) {
      await this.audioItem.seekToPageTime(pageTime);
    }
  }

  private syncAudioPlaybackState(): void {
    if (this.audioItem.shouldBePlaying() && !this.audioItem.audioPlayer.isPlaying()) {
      this.audioItem.play();
    } else if (!this.audioItem.shouldBePlaying() && this.audioItem.audioPlayer.isPlaying()) {
      this.audioItem.audioPlayer.pause();
    }
  }
}
